import { formatDateTime } from "../chart/util";
import { confirmDialog } from "../practice/dialog";
import { wireZoneIdChips, zoneIdChip, zoneIdOf } from "../practice/zoneId";
import {
  deleteMaster,
  editMaster,
  fetchActivity,
  listMaster,
  exportMasterCSV,
  exportMasterJSON,
  resetMaster,
  restoreMaster,
  type Activity,
  type ListQuery,
  type MasterRow,
} from "../practice/master";

// Admin → master practice data: daily practice activity per user, browse/edit/delete/
// restore every marking in the master DB, and a backed-up reset.

const PAGE = 50;
const ACTIVITY_DAYS = 30;

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const signed = (v: number) => (v > 0 ? "+" : v < 0 ? "−" : "") + Math.abs(v).toFixed(2);
const timeOf = (ms: number) => new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const dateOf = (ms: number) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function fmtDur(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}
function relTime(ms: number | null): string {
  if (!ms) return "never";
  const s = (Date.now() - ms) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const OUTCOME: Record<string, string> = { tp: "🎯 Target", sl: "🛑 Stop", no_fill: "⏸️ No fill", open: "⏳ Open" };

export function mountPracticeAdmin(host: HTMLElement, me: string, users: string[]) {
  const userOpts = users.map((u) => `<option value="${esc(u)}">${esc(u)}</option>`).join("");
  host.innerHTML = `
    <section class="pa">
      <div class="card pa-card">
        <div class="pa-head">
          <h3>Practice activity</h3>
          <span class="pa-sub">last ${ACTIVITY_DAYS} days · your local time</span>
          <span class="pa-spacer"></span>
          <button class="mini-btn" data-pa="refresh">↻ Refresh</button>
        </div>
        <div id="pa-activity" class="pa-loading">Loading…</div>
      </div>

      <div class="card pa-card">
        <div class="pa-head">
          <h3>Master practice data</h3>
          <span class="pa-sub" id="pa-totals"></span>
          <span class="pa-spacer"></span>
          <button class="mini-btn" data-pa="csv">⬇ CSV</button>
          <button class="mini-btn" data-pa="json">⬇ JSON</button>
        </div>
        <div class="pa-filters">
          <select id="pa-user" aria-label="User"><option value="">All users</option>${userOpts}</select>
          <select id="pa-decision" aria-label="Decision">
            <option value="">Any decision</option><option value="approved">👍 Approved</option><option value="rejected">👎 Rejected</option>
          </select>
          <select id="pa-outcome" aria-label="Outcome">
            <option value="">Any outcome</option><option value="tp">🎯 Target</option><option value="sl">🛑 Stop</option>
            <option value="no_fill">⏸️ No fill</option><option value="open">⏳ Open</option>
          </select>
          <select id="pa-deleted" aria-label="Show">
            <option value="exclude">Live</option><option value="only">Deleted</option><option value="include">Live + deleted</option>
          </select>
          <input id="pa-q" type="search" placeholder="Search notes, user, type, TF or zone ID…" />
          <span class="pa-spacer"></span>
          <button class="mini-btn danger" id="pa-bulk-del" disabled>Delete selected</button>
        </div>
        <div class="pa-tablewrap">
          <table class="pa-table">
            <thead><tr>
              <th><input type="checkbox" id="pa-all" aria-label="Select all" /></th>
              <th>Marked</th><th>Think time</th><th>User</th><th>Zone</th><th>Decision</th><th>Note</th><th>Outcome</th><th>R</th><th>Flags</th><th></th>
            </tr></thead>
            <tbody id="pa-rows"><tr><td colspan="11" class="pa-loading">Loading…</td></tr></tbody>
          </table>
        </div>
        <div class="pa-foot"><span id="pa-count"></span><button class="mini-btn" id="pa-more" hidden>Load more</button></div>
      </div>

      <div class="card pa-card pa-danger">
        <h3>Reset master data</h3>
        <p class="pa-sub">Permanently removes markings from the master database. Every removed row is copied to the cloud archive table first, so it can be recovered.</p>
        <div class="pa-reset-row">
          <select id="pa-reset-scope" aria-label="Reset scope"><option value="">All users</option>${userOpts}</select>
          <input id="pa-reset-confirm" placeholder="Type RESET to confirm" autocomplete="off" />
          <button class="mini-btn pa-reset-btn" id="pa-reset" disabled>Reset</button>
        </div>
        <div id="pa-reset-result" class="pa-sub"></div>
      </div>
    </section>`;

  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => host.querySelector<T>(sel)!;
  const tbody = $("#pa-rows");
  let rows: MasterRow[] = [];
  let total = 0;
  const selected = new Set<string>();

  const query = (): ListQuery => ({
    markedBy: $<HTMLSelectElement>("#pa-user").value,
    decision: $<HTMLSelectElement>("#pa-decision").value,
    outcome: $<HTMLSelectElement>("#pa-outcome").value,
    deleted: $<HTMLSelectElement>("#pa-deleted").value as ListQuery["deleted"],
    q: $<HTMLInputElement>("#pa-q").value.trim(),
  });

  // ---------------------------------------------------------------- activity
  async function loadActivity() {
    const box = $("#pa-activity");
    let act: Activity;
    try {
      act = await fetchActivity(ACTIVITY_DAYS, users);
    } catch (e) {
      box.className = "pa-error";
      box.textContent = (e as Error).message;
      return;
    }
    box.className = "";
    const level = (n: number) => (n === 0 ? 0 : n <= 2 ? 1 : n <= 5 ? 2 : n <= 10 ? 3 : 4);
    const dayLabel = (k: string) => new Date(k + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const userRows = act.users
      .map((u) => {
        const cells = act.days
          .map((k) => {
            const d = u.byDay[k];
            const tip = d ? `${dayLabel(k)}: ${d.count} marking${d.count === 1 ? "" : "s"} · ${timeOf(d.first)}–${timeOf(d.last)} · ${fmtDur(d.activeMs)} practicing` : `${dayLabel(k)}: no practice`;
            return `<span class="pa-cell l${d ? level(d.count) : 0}${k === act.today ? " is-today" : ""}" title="${esc(tip)}"></span>`;
          })
          .join("");
        return `
          <div class="pa-act-row">
            <div class="pa-act-user">
              <b>${esc(u.user)}</b>
              <span class="pa-today ${u.practicedToday ? "yes" : "no"}">${u.practicedToday ? "✓ Practiced today" : "Not yet today"}</span>
            </div>
            <div class="pa-heat" role="img" aria-label="${esc(u.user)} practice over ${ACTIVITY_DAYS} days">${cells}</div>
            <div class="pa-act-stats">
              <span title="Consecutive days with practice">🔥 <b>${u.streak}</b> day streak</span>
              <span title="Days with at least one marking"><b>${u.activeDays}</b>/${ACTIVITY_DAYS} days</span>
              <span><b>${u.total}</b> marks</span>
              <span title="Summed think time (each marking capped at 10 min)">⏱ <b>${fmtDur(u.activeMs)}</b></span>
              <span title="${u.lastMarkedAt ? new Date(u.lastMarkedAt).toLocaleString() : ""}">last ${relTime(u.lastMarkedAt)}</span>
            </div>
          </div>`;
      })
      .join("");
    const first = act.days[0];
    box.innerHTML = `
      ${userRows || `<div class="pa-empty">No users yet.</div>`}
      <div class="pa-heat-axis"><span>${dayLabel(first)}</span><span class="pa-legend">less <i class="pa-cell l0"></i><i class="pa-cell l1"></i><i class="pa-cell l2"></i><i class="pa-cell l3"></i><i class="pa-cell l4"></i> more</span><span>Today</span></div>`;
  }

  // ---------------------------------------------------------------- records
  function rowHTML(r: MasterRow): string {
    const deleted = r.deleted_at != null;
    const flags = [
      r.previewed ? `<span class="pr-flag sm" title="Outcome previewed before deciding">👀</span>` : "",
      r.original_decision && r.original_decision !== r.decision ? `<span class="pr-flag sm" title="Original decision">was ${r.original_decision === "approved" ? "👍" : "👎"}</span>` : "",
      r.edited_at ? `<span class="pr-flag sm" title="Edited ${new Date(r.edited_at).toLocaleString()}">✎ ${esc(r.edited_by ?? "")}</span>` : "",
      deleted ? `<span class="pr-flag sm is-del" title="Deleted ${new Date(r.deleted_at!).toLocaleString()}">🗑 ${esc(r.deleted_by ?? "")}</span>` : "",
    ].join("");
    const rCls = r.filled ? ((r.r_multiple ?? 0) >= 0 ? "pnl-pos" : "pnl-neg") : "";
    const sideCls = r.side === "long" ? "long" : "short";
    return `
      <tr data-id="${esc(r.id)}" class="${deleted ? "is-deleted" : ""}">
        <td><input type="checkbox" class="pa-sel" data-id="${esc(r.id)}" ${selected.has(r.id) ? "checked" : ""} aria-label="Select" /></td>
        <td><b>${dateOf(r.marked_at)}</b><span class="pa-sub">${timeOf(r.marked_at)}</span></td>
        <td>${r.decide_ms != null ? fmtDur(r.decide_ms) : "—"}${r.shown_at ? `<span class="pa-sub">from ${timeOf(r.shown_at)}</span>` : ""}</td>
        <td>${esc(r.marked_by)}</td>
        <td><span class="pa-zone ${sideCls}">${esc(r.zone_type ?? "?")}</span> b${r.boring}·L${r.leg_outs}<span class="pa-sub">${esc(r.detect_tf ?? "")} · ${r.signal_time ? formatDateTime(r.signal_time) + " ET" : ""}</span>${r.zone_type && r.detect_tf && r.signal_time ? zoneIdChip(zoneIdOf(r.zone_type, r.detect_tf, r.signal_time)) : ""}</td>
        <td><select class="pa-dec" data-id="${esc(r.id)}" ${deleted ? "disabled" : ""}>
              <option value="approved" ${r.decision === "approved" ? "selected" : ""}>👍 Approved</option>
              <option value="rejected" ${r.decision === "rejected" ? "selected" : ""}>👎 Rejected</option>
            </select></td>
        <td><input class="pa-note" data-id="${esc(r.id)}" value="${esc(r.note ?? "")}" placeholder="—" ${deleted ? "disabled" : ""} /></td>
        <td>${r.outcome ? OUTCOME[r.outcome] ?? r.outcome : "—"}</td>
        <td class="${rCls}">${r.filled && r.r_multiple != null ? signed(r.r_multiple) + "R" : "—"}</td>
        <td class="pa-flags">${flags}</td>
        <td class="pa-actions">${
          deleted
            ? `<button class="mini-btn" data-restore="${esc(r.id)}">↺ Restore</button>`
            : `<button class="mini-btn primary" data-save="${esc(r.id)}" disabled>Save</button><button class="an-icon del" data-del="${esc(r.id)}" title="Delete">✕</button>`
        }</td>
      </tr>`;
  }

  function renderRows() {
    tbody.innerHTML = rows.length ? rows.map(rowHTML).join("") : `<tr><td colspan="11" class="pa-empty">No markings match.</td></tr>`;
    $("#pa-count").textContent = total ? `Showing ${rows.length} of ${total}` : "";
    $("#pa-more").hidden = rows.length >= total;
    updateBulk();
  }

  async function loadRows(append = false) {
    try {
      const res = await listMaster({ ...query(), limit: PAGE, offset: append ? rows.length : 0 });
      total = res.total;
      rows = append ? rows.concat(res.rows) : res.rows;
      if (!append) selected.clear();
      renderRows();
      $("#pa-totals").textContent = `${total} marking${total === 1 ? "" : "s"} match`;
    } catch (e) {
      tbody.innerHTML = `<tr><td colspan="11" class="pa-error">${esc((e as Error).message)}</td></tr>`;
    }
  }

  const refreshAll = () => Promise.all([loadRows(), loadActivity()]);

  function rowDirty(id: string): boolean {
    const r = rows.find((x) => x.id === id);
    const tr = tbody.querySelector<HTMLElement>(`tr[data-id="${CSS.escape(id)}"]`);
    if (!r || !tr) return false;
    const dec = tr.querySelector<HTMLSelectElement>(".pa-dec")!.value;
    const note = tr.querySelector<HTMLInputElement>(".pa-note")!.value.trim();
    return dec !== r.decision || note !== (r.note ?? "");
  }

  function updateBulk() {
    const b = $<HTMLButtonElement>("#pa-bulk-del");
    b.disabled = selected.size === 0;
    b.textContent = selected.size ? `Delete selected (${selected.size})` : "Delete selected";
    const all = $<HTMLInputElement>("#pa-all");
    const live = rows.filter((r) => r.deleted_at == null);
    all.checked = live.length > 0 && live.every((r) => selected.has(r.id));
  }

  async function save(id: string) {
    const tr = tbody.querySelector<HTMLElement>(`tr[data-id="${CSS.escape(id)}"]`);
    if (!tr) return;
    const decision = tr.querySelector<HTMLSelectElement>(".pa-dec")!.value;
    const note = tr.querySelector<HTMLInputElement>(".pa-note")!.value;
    try {
      await editMaster(id, me, { decision, note });
      await loadRows();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  // ---------------------------------------------------------------- events
  host.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    if (t.classList.contains("pa-note")) {
      const id = (t as HTMLInputElement).dataset.id!;
      const btn = tbody.querySelector<HTMLButtonElement>(`[data-save="${CSS.escape(id)}"]`);
      if (btn) btn.disabled = !rowDirty(id);
    } else if (t.id === "pa-reset-confirm") {
      $<HTMLButtonElement>("#pa-reset").disabled = (t as HTMLInputElement).value !== "RESET";
    }
  });

  let searchTimer = 0;
  host.addEventListener("change", (e) => {
    const t = e.target as HTMLElement;
    if (t.classList.contains("pa-dec")) {
      const id = (t as HTMLSelectElement).dataset.id!;
      const btn = tbody.querySelector<HTMLButtonElement>(`[data-save="${CSS.escape(id)}"]`);
      if (btn) btn.disabled = !rowDirty(id);
    } else if (t.classList.contains("pa-sel")) {
      const id = (t as HTMLInputElement).dataset.id!;
      if ((t as HTMLInputElement).checked) selected.add(id);
      else selected.delete(id);
      updateBulk();
    } else if (t.id === "pa-all") {
      const on = (t as HTMLInputElement).checked;
      for (const r of rows) if (r.deleted_at == null) on ? selected.add(r.id) : selected.delete(r.id);
      renderRows();
    } else if (["pa-user", "pa-decision", "pa-outcome", "pa-deleted"].includes(t.id)) {
      loadRows();
    }
  });
  $("#pa-q").addEventListener("input", () => {
    clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => loadRows(), 250);
  });

  host.addEventListener("keydown", (e) => {
    const t = e.target as HTMLElement;
    if (t.classList.contains("pa-note") && e.key === "Enter") {
      e.preventDefault();
      const id = (t as HTMLInputElement).dataset.id!;
      if (rowDirty(id)) save(id);
    }
  });

  host.addEventListener("click", async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button");
    if (!btn) return;
    if (btn.dataset.pa === "refresh") return void refreshAll();
    if (btn.dataset.pa === "csv" || btn.dataset.pa === "json") {
      try {
        await (btn.dataset.pa === "csv" ? exportMasterCSV() : exportMasterJSON());
      } catch (err) {
        alert((err as Error).message);
      }
      return;
    }
    if (btn.id === "pa-more") return void loadRows(true);
    if (btn.dataset.save) return void save(btn.dataset.save);

    if (btn.dataset.restore) {
      try {
        await restoreMaster(btn.dataset.restore, me);
        await refreshAll();
      } catch (err) {
        alert((err as Error).message);
      }
      return;
    }

    if (btn.dataset.del || btn.id === "pa-bulk-del") {
      const ids = btn.dataset.del ? [btn.dataset.del] : [...selected];
      if (!ids.length) return;
      const ok = await confirmDialog({
        emoji: "🗑️",
        title: ids.length === 1 ? "Delete this marking?" : `Delete ${ids.length} markings?`,
        body: "They're hidden from the dataset and exports, and removed from the user's history. You can restore them from the Deleted view.",
        confirm: "Delete",
        danger: true,
      });
      if (!ok) return;
      try {
        await Promise.all(ids.map((id) => deleteMaster(id, me)));
        await refreshAll();
      } catch (err) {
        alert((err as Error).message);
      }
      return;
    }

    if (btn.id === "pa-reset") {
      const scope = $<HTMLSelectElement>("#pa-reset-scope").value;
      const ok = await confirmDialog({
        emoji: "⚠️",
        title: scope ? `Reset all of ${scope}'s markings?` : "Reset the entire master database?",
        body: `This permanently deletes ${scope ? `every marking by ${scope}` : "every marking from every user"}. Every removed row is archived in the cloud first.`,
        confirm: "Reset now",
        danger: true,
      });
      if (!ok) return;
      const out = $("#pa-reset-result");
      try {
        const r = await resetMaster(me, scope || undefined);
        out.textContent = `Deleted ${r.deleted} marking${r.deleted === 1 ? "" : "s"} (${r.scope}). Backup: ${r.backup}`;
        $<HTMLInputElement>("#pa-reset-confirm").value = "";
        $<HTMLButtonElement>("#pa-reset").disabled = true;
        await refreshAll();
      } catch (err) {
        out.textContent = (err as Error).message;
      }
    }
  });

  wireZoneIdChips();
  refreshAll();
}
